// =============================================================================
// writeVerify — pure "did the write reach its goal state?" predicates.
//
// After an UNCERTAIN reply (the flaky /exec HTML-at-200 case), each FOH write
// confirms itself by re-reading the SAME feed the tablet reads and checking the
// goal state. These predicates are the goal checks, kept pure so they can be unit
// tested without a browser. Each is fed the parsed feed JSON (or null if the
// re-read never got real JSON — see backendJson.fetchJsonWithRetry).
//
// The feed shapes and the per-action reconstruction were traced against the live
// Apps Script backend (ManchesterForecastEngine.js / ManchesterReportWriter.js).
// Two non-obvious facts drive the checks below:
//   • A RECEIVED stock item stays in `getstockrequests` the SAME day as
//     closed:true — so "did the receive land?" is "the item has no OPEN row",
//     NOT "the item is gone".
//   • Sell-out UNDO pops the last cycle, so a landed undo shows exactly one
//     fewer cycle for the SKU.
// =============================================================================

// EOD: today's report exists (submitted:true) in the eodToday feed.
export function eodSubmitted(eodToday) {
  return !!(eodToday && eodToday.submitted);
}

// Sell-out: is this SKU currently sold out? `sellOutTimes` holds ONLY SKUs whose
// latest cycle is still open, so its presence == "currently out".
export function skuSoldOut(sellOutToday, sku) {
  return !!(sellOutToday && sellOutToday.sellOutTimes && sellOutToday.sellOutTimes[sku] != null);
}

// Sell-out: how many cycles the feed shows for this SKU. A landed undo pops one
// cycle, so the caller confirms undo by expecting `priorCount - 1`.
export function sellOutCycleCount(sellOutToday, sku) {
  const cy = sellOutToday && sellOutToday.sellOutCycles && sellOutToday.sellOutCycles[sku];
  return Array.isArray(cy) ? cy.length : 0;
}

// Stock: does this item have an OPEN (not-closed) request in the getstockrequests
// feed? This is the right question for BOTH directions: a landed ADD makes it
// true; a landed UNDO or RECEIVE makes it false (a received row lingers in the
// feed as closed:true, so we must look at `closed`, not mere presence). Item
// names are matched trimmed + case-insensitive.
// NOTE: this is the raw feed state (`!closed`), which is intentionally broader
// than the Needed-list's UI visibility rule (an out-of-stock-not-ordered row is
// hidden to staff but is still "open" here). That's correct: verify confirms the
// write reached the BACKEND, not that the row is currently shown on screen.
export function stockHasOpenRequest(getStockRequests, item) {
  const rows = (getStockRequests && getStockRequests.requests) || [];
  const norm = (s) => String(s == null ? "" : s).trim().toLowerCase();
  const target = norm(item);
  if (!target) return false;
  return rows.some((r) => r && norm(r.item) === target && !r.closed);
}
